import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { incomingInvoiceCostAllocationFormSchema } from "@msuite/utils/schemas";
import type { IncomingInvoiceDetail } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import {
	allocationCents,
	type CostAllocationFormValues,
	IncomingInvoiceCostAllocationForm,
	shareOf,
} from "@/forms/incoming-invoice-cost-allocation-form";
import { client } from "@/hono";

/** Props Interface */
interface EditCostAllocationModalProps extends ModalProps {
	incomingInvoice: IncomingInvoiceDetail;
	/** The invoice's net amount; the modal is only offered for an invoice that has one. */
	netAmount: number;
}

export function EditCostAllocationModal({
	isOpen,
	onClose,
	incomingInvoice,
	netAmount,
}: EditCostAllocationModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const methods = useForm<CostAllocationFormValues>({
		defaultValues: {
			allocations: incomingInvoice.costAllocations.map((allocation) => ({
				costCenterId: allocation.costCenter.id,
				amount: allocation.amount,
				share: shareOf(allocation.amount, netAmount),
			})),
		},
		resolver: resolver(incomingInvoiceCostAllocationFormSchema),
	});

	/** Functions */
	async function handleOnSubmit(values: CostAllocationFormValues) {
		// Only the amounts are stored; the shares were the form's way of entering them.
		const json = {
			allocations: values.allocations.map(({ costCenterId, amount }) => ({ costCenterId, amount })),
		};
		// The server refuses a split that misses the net amount; saying so here keeps the modal open
		// with the rows as they are, instead of a round trip that ends in a toast.
		const cents = allocationCents(json.allocations.map((allocation) => allocation.amount));
		if (json.allocations.length && cents !== Math.round(netAmount * 100)) {
			methods.setError("root", { message: t("incoming_invoices.cost_allocation_must_add_up") });
			return;
		}
		const response = await client.incoming_invoices[":incomingInvoiceId"][
			"cost-allocations"
		].$patch({ param: { incomingInvoiceId: incomingInvoice.id }, json });
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["incoming-invoices"]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("incoming_invoices.edit_cost_allocation")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
				width="48rem"
			>
				<Modal.Body>
					<IncomingInvoiceCostAllocationForm
						netAmount={netAmount}
						currency={incomingInvoice.currency}
					/>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
